/* ⚠️ GENERATED from app/services/avatarPose.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarPose.ts — how the avatar stands, and the six ways it can stand.
//
// 🟥 A POSE IS NOT A MODEL. Baking one would mean six bodies × six poses of
//    geometry to download; and it would freeze the pose out of reach of the
//    motion code. A pose here is a few numbers applied to the ASSEMBLED
//    skeleton at mount time: nothing extra is fetched, every part follows
//    because every part is bound to those same bones, and the wave / peek /
//    nod still run on top of it.
//
// 🟥 AND IT IS NOT A LIST OF EULER ANGLES. Hand-authored angles compound: the
//    same numbers that put one body's hand on its hip put a taller, heavier
//    body's hand inside its thigh, and a small mistake early in a chain becomes
//    an arm through the chest. A pose says WHERE THE HAND GOES, in the figure's
//    own proportions, and a two-bone IK solves the shoulder and the elbow to
//    put it there. That is stable for every body, every build, every height.
//
// 🟢 THE REST POSE IS ALREADY RELAXED (arms down, elbows soft, fingers curled —
//    baked into the catalogue), so `relaxed` is the empty pose and everything
//    else is a departure from it.
//
// 🟢 PURE THREE. A twin is generated for the web; the tests run the solver
//    against the real catalogue bodies.

import * as THREE from 'three';

/** Where a hand goes: an offset FROM THAT ARM'S OWN SHOULDER, measured in arm
 *  lengths (shoulder→elbow→wrist).
 *
 *  🟢 THE SHOULDER AND THE ARM ARE THE ONLY HONEST RULER. A fraction of the
 *     bounding box moves when the arms move; a fraction of height is wrong for
 *     a short body with long arms. "Three-quarters of an arm down and a little
 *     across" puts the hand on the hip of every body in the catalogue, at every
 *     build and height, because the hip IS three-quarters of an arm down.
 *
 *  x: + is that arm's own side, − is across the body
 *  y: + is up
 *  z: + is forward (toward the viewer) */

/* ─── the six ─────────────────────────────────────────────────────────────
   🟢 Named for what a person would call them, and chosen to cover what a
      profile picture is ever asked to do: stand, lean, greet, wait. */
export const POSES = [{
  id: 'relaxed'
}, {
  /* a hand on the hip, weight on one leg — the way people stand when waiting */
  id: 'hip',
  spine: [0, 0, 2.5],
  head: [0, 5, -3],
  stance: 4,
  left: {
    x: -0.06,
    y: -0.62,
    z: 0.06,
    elbow: 'back',
    curl: 0.4
  }
}, {
  /* both hands on the hips, feet apart */
  id: 'hero',
  spine: [-2, 0, 0],
  head: [2, 0, 0],
  stance: 8,
  left: {
    x: -0.06,
    y: -0.62,
    z: 0.06,
    elbow: 'back',
    curl: 0.4
  },
  right: {
    x: -0.06,
    y: -0.62,
    z: 0.06,
    elbow: 'back',
    curl: 0.4
  }
}, {
  /* arms folded, forearms clear of the chest so the shirt follows */
  id: 'crossed',
  head: [0, 0, 2],
  left: {
    x: -0.70,
    y: -0.20,
    z: 0.20,
    elbow: 'down',
    curl: 0.3
  },
  right: {
    x: -0.56,
    y: -0.30,
    z: 0.14,
    elbow: 'down',
    curl: 0.3
  }
}, {
  /* hands clasped behind the back */
  id: 'behind',
  spine: [-2.5, 0, 0],
  left: {
    x: -0.30,
    y: -0.58,
    z: -0.46,
    elbow: 'out',
    curl: 0.45
  },
  right: {
    x: -0.30,
    y: -0.58,
    z: -0.46,
    elbow: 'out',
    curl: 0.45
  }
}, {
  /* an open hand up beside the head */
  id: 'wave',
  head: [0, -3, -3],
  right: {
    x: 0.34,
    y: 0.60,
    z: 0.10,
    elbow: 'down',
    curl: -0.8,
    twist: -8
  }
}, {
  /* a hand at the chin, head tilted — waiting, listening */
  id: 'think',
  head: [4, -6, 4],
  spine: [0, 0, 1.5],
  right: {
    x: -0.24,
    y: 0.34,
    z: 0.22,
    elbow: 'down',
    curl: 0.55
  }
}];
export const POSE_IDS = POSES.map(p => p.id);
export const DEFAULT_POSE = 'relaxed';
export function poseById(id) {
  return POSES.find(p => p.id === id) || POSES[0];
}

/* ─── the rig the solver needs ────────────────────────────────────────── */

const NORM = n => String(n || '').toLowerCase().replace(/[\s:._]/g, '');
export function findPoseBone(root, name) {
  const want = NORM(name);
  let hit = null;
  root.traverse(o => {
    if (!hit && o.isBone && NORM(o.name) === want) hit = o;
  });
  return hit;
}
const FINGERS = ['Index', 'Middle', 'Ring', 'Pinky'];
export function poseRigOf(root, landmarks) {
  const side = s => ({
    arm: findPoseBone(root, `mixamorig${s}Arm`),
    fore: findPoseBone(root, `mixamorig${s}ForeArm`),
    hand: findPoseBone(root, `mixamorig${s}Hand`),
    fingers: FINGERS.flatMap(f => [1, 2, 3].map(j => findPoseBone(root, `mixamorig${s}Hand${f}${j}`)).filter(Boolean)),
    thumb: [1, 2, 3].map(j => findPoseBone(root, `mixamorig${s}HandThumb${j}`)).filter(Boolean)
  });
  const top = Number(landmarks?.top);
  const floor = Number(landmarks?.floor);
  const hw = Number(landmarks?.halfWidth);
  return {
    root,
    hips: findPoseBone(root, 'mixamorigHips'),
    spine: findPoseBone(root, 'mixamorigSpine'),
    spine1: findPoseBone(root, 'mixamorigSpine1'),
    head: findPoseBone(root, 'mixamorigHead'),
    arms: {
      Left: side('Left'),
      Right: side('Right')
    },
    legs: {
      Left: findPoseBone(root, 'mixamorigLeftUpLeg'),
      Right: findPoseBone(root, 'mixamorigRightUpLeg')
    },
    height: Number.isFinite(top) && Number.isFinite(floor) ? top - floor : 1.75,
    halfWidth: Number.isFinite(hw) ? hw : 0.32,
    centreY: Number.isFinite(floor) ? floor : 0
  };
}

/* ─── keeping the hands OUT OF THE BODY ──────────────────────────────────

   🟥 THE SOLVER PUTS THE WRIST ON THE TARGET, AND WHAT CLIPS IS THE HAND.
      Reported: "the hands in most of the poses are inside the body" — fingers
      through the waistband on `hip`, through the upper arms on `crossed`, the
      whole hand swallowed by the pelvis on `behind`. Every one of those targets
      was hand-placed against ONE body, and then eleven body dimensions were
      added on top of it. Widen the hips and the hand that rested on them is
      inside them; nothing in the system ever knew the body had a width.

   🟢 So it is given one, and MEASURED FROM THE RIG rather than typed in — the
      same argument the arm-length ruler above already makes. The shoulder joint
      sits a known distance from the midline and the hip a known distance below
      it, so the torso's half-width at any height is a number this file can work
      out for whatever body it has been handed, at every build and height.

   🟥 AND THE HAND IS NOT A POINT. It reaches about a fifth of an arm past the
      wrist and is about a fifteenth thick, so clearing the wrist is not
      clearing the hand. Both are in the budget below. */

/** Half the hand's thickness, in arm lengths — the wrist must clear by at least this. */
export const HAND_HALF = 0.085;

/**
 * The torso's half-width at a height `y` arm-lengths below the shoulder.
 *
 * `S` is the shoulder joint's own distance from the midline (arm lengths) and
 * `hipDrop` is how far below the shoulder the hip sits (negative, arm lengths).
 *
 * 🟢 The joint is not the edge of the body: the shoulder BONE starts inboard of
 *    the deltoid, so the flesh reaches a little past it at the top and pulls in
 *    toward the hip. Two measured ratios, linear between them, flat outside.
 */
export function bodyHalfWidth(y, S, hipDrop) {
  if (!(S > 0)) return 0;
  const d = hipDrop < -1e-6 ? hipDrop : -0.55;
  const t = Math.max(0, Math.min(1, y / d)); // 0 at the shoulder, 1 at the hip
  return S * (0.86 + (0.70 - 0.86) * t);
}

/** The torso's half-DEPTH at the same height — a body is an ellipse, not a disc. */
export function bodyHalfDepth(y, S, hipDrop) {
  if (!(S > 0)) return 0;
  const d = hipDrop < -1e-6 ? hipDrop : -0.55;
  const t = Math.max(0, Math.min(1, y / d));
  return S * (0.62 + (0.58 - 0.62) * t);
}

/**
 * Push a hand target out until the hand clears the body.
 *
 * 🟥 `t.x` IS MEASURED FROM THE SHOULDER, NOT THE MIDLINE, which is what made
 *    the old numbers so easy to get wrong: `hip` asked for x = −0.06, which
 *    reads as "a little across the body" and actually lands the wrist a whisker
 *    INSIDE the hip on any build wider than the one it was tuned on.
 *
 * 🟥 AND IT IS PUSHED ALONG THE DIRECTION IT ALREADY POINTS, not sideways. A
 *    naive "move it outboard in x" would drag the clasped hands of `behind`
 *    apart and uncross the folded arms of `crossed` — it would fix one pose by
 *    destroying two. The torso is an ellipse in plan; a hand inside it is moved
 *    radially out of it, so a hand in front goes further forward, a hand behind
 *    goes further back, and a hand at the side goes further out. Every pose
 *    keeps its own intent.
 *
 * 🟢 Only ever outward. A target already clear of the body is returned
 *    untouched — `behind` reaches k = 1.53 and is never touched at all.
 */
export function clearedTarget(t, S, hipDrop) {
  if (!t || !(S > 0)) return t;
  const W = bodyHalfWidth(t.y, S, hipDrop) + HAND_HALF;
  const D = bodyHalfDepth(t.y, S, hipDrop) + HAND_HALF;
  if (!(W > 0) || !(D > 0)) return t;
  const x = S + t.x; // the wrist's own distance from the midline
  const z = t.z || 0;
  const k = Math.sqrt(x / W * (x / W) + z / D * (z / D));
  if (k >= 1) return t; // already outside the torso
  if (k < 1e-6) return {
    ...t,
    x: t.x + (W - x)
  }; // dead on the axis: out to the side
  const f = 1 / k;
  return {
    ...t,
    x: t.x + x * (f - 1),
    z: z * f
  };
}

/** The rig's own measurements, in arm lengths: how wide the shoulders are and how far down the hip is. */
export function bodyMetrics(rig, side) {
  const a = rig.arms[side];
  const L = armLength(rig, side);
  if (!a.arm || !rig.hips || !(L > 1e-4)) return {
    S: 0.375,
    hipDrop: -0.55
  };
  const sh = new THREE.Vector3(),
    hp = new THREE.Vector3();
  a.arm.getWorldPosition(sh);
  rig.hips.getWorldPosition(hp);
  const S = Math.abs(sh.x - hp.x) / L;
  const hipDrop = (hp.y - sh.y) / L;
  return {
    S: S > 0.05 ? S : 0.375,
    hipDrop: hipDrop < -0.05 ? hipDrop : -0.55
  };
}

/* ─── the solver ──────────────────────────────────────────────────────── */

/* 🟥 SCRATCH IS PER FUNCTION, NEVER SHARED ACROSS A CALL THAT NESTS. `solveArm`
   holds the shoulder and the goal direction across two `aimWorld` calls; one
   shared pool and the helper silently overwrites the caller's own vectors —
   which is how an arm ends up a quarter of a metre from where it was sent. */
const _w1 = new THREE.Vector3(),
  _w2 = new THREE.Vector3(),
  _w3 = new THREE.Vector3(),
  _w4 = new THREE.Vector3();
const _wq = new THREE.Quaternion(),
  _wqp = new THREE.Quaternion(),
  _wqi = new THREE.Quaternion();

/** Give `bone` a new WORLD rotation `delta ∘ current`, written into its local quaternion. */
function applyWorldDelta(bone, delta) {
  const parent = bone.parent;
  if (parent) {
    parent.updateWorldMatrix(true, false);
    parent.getWorldQuaternion(_wqp);
    _wqi.copy(_wqp).invert();
  } else _wqi.identity();
  bone.getWorldQuaternion(_wqp);
  bone.quaternion.copy(_wqi).multiply(delta).multiply(_wqp);
  bone.updateMatrixWorld(true);
}

/** Turn `bone` so the direction from it toward `child` points along `dir` (world). */
function aimWorld(bone, child, dir) {
  bone.updateWorldMatrix(true, false);
  child.updateWorldMatrix(true, false);
  bone.getWorldPosition(_w1);
  child.getWorldPosition(_w2);
  _w3.copy(_w2).sub(_w1);
  if (_w3.lengthSq() < 1e-12 || dir.lengthSq() < 1e-12) return;
  _w3.normalize();
  _w4.copy(dir).normalize();
  _wq.setFromUnitVectors(_w3, _w4);
  applyWorldDelta(bone, _wq);
}

/** Rotate `bone` about its own head, around a world axis, by `deg`. */
export function rotateWorld(bone, axis, deg) {
  if (!bone || !deg) return;
  bone.updateWorldMatrix(true, false);
  _wq.setFromAxisAngle(_w4.copy(axis).normalize(), THREE.MathUtils.degToRad(deg));
  applyWorldDelta(bone, _wq);
}
const AX_X = new THREE.Vector3(1, 0, 0);
const AX_Y = new THREE.Vector3(0, 1, 0);
const AX_Z = new THREE.Vector3(0, 0, 1);

/** The length of an arm, shoulder → elbow → wrist, at rest. */
export function armLength(rig, side) {
  const a = rig.arms[side];
  if (!a.arm || !a.fore || !a.hand) return 0.48;
  const p = new THREE.Vector3(),
    q = new THREE.Vector3(),
    r = new THREE.Vector3();
  a.arm.getWorldPosition(p);
  a.fore.getWorldPosition(q);
  a.hand.getWorldPosition(r);
  const len = p.distanceTo(q) + q.distanceTo(r);
  return len > 1e-4 ? len : 0.48;
}

/** A hand target (arm lengths from that shoulder) → a world point. */
export function handWorld(rig, side, t) {
  const sgn = side === 'Left' ? 1 : -1;
  const a = rig.arms[side];
  const shoulder = new THREE.Vector3();
  if (a.arm) {
    a.arm.updateWorldMatrix(true, false);
    a.arm.getWorldPosition(shoulder);
  }
  const L = armLength(rig, side);
  return shoulder.add(new THREE.Vector3(sgn * t.x * L, t.y * L, t.z * L));
}

/**
 * Two-bone IK: turn the shoulder and the elbow so the wrist lands on `target`.
 *
 * 🟢 ANALYTIC, NOT ITERATIVE. The law of cosines gives the elbow angle exactly;
 *    a target further than the arm can reach simply straightens it. There is no
 *    loop to not converge and no configuration that explodes.
 */
export function solveArm(rig, side, t) {
  const a = rig.arms[side];
  if (!a.arm || !a.fore || !a.hand) return false;
  const sgn = side === 'Left' ? 1 : -1;
  rig.root.updateMatrixWorld(true);
  /* own locals: aimWorld() below reuses the module scratch */
  const shoulder = new THREE.Vector3(),
    elbow = new THREE.Vector3(),
    wrist = new THREE.Vector3();
  a.arm.getWorldPosition(shoulder);
  a.fore.getWorldPosition(elbow);
  a.hand.getWorldPosition(wrist);
  const upper = shoulder.distanceTo(elbow);
  const lower = elbow.distanceTo(wrist);
  if (!(upper > 0 && lower > 0)) return false;
  const goal = handWorld(rig, side, t);
  const dir = new THREE.Vector3().copy(goal).sub(shoulder);
  let dist = dir.length();
  if (dist < 1e-4) return false;
  const maxReach = (upper + lower) * 0.995;
  const minReach = Math.abs(upper - lower) * 1.05 + 1e-3;
  if (dist > maxReach) dist = maxReach;else if (dist < minReach) dist = minReach;
  dir.normalize();

  // the pole: which way the elbow is pushed
  const pole = new THREE.Vector3();
  switch (t.elbow || 'out') {
    case 'down':
      pole.set(sgn * 0.35, -1, -0.15);
      break;
    case 'back':
      pole.set(sgn * 0.55, -0.75, -0.6);
      break;
    case 'up':
      pole.set(sgn * 0.5, 0.8, -0.3);
      break;
    default:
      pole.set(sgn * 1, -0.45, -0.25);
      break;
    // out
  }
  pole.normalize();
  // the component of the pole perpendicular to the shoulder→goal line
  const perp = new THREE.Vector3().copy(pole).addScaledVector(dir, -pole.dot(dir));
  if (perp.lengthSq() < 1e-6) {
    perp.set(sgn, -0.3, -0.2).addScaledVector(dir, -perp.dot(dir));
  }
  perp.normalize();

  // law of cosines: the angle at the shoulder between the goal line and the upper arm
  const cosA = Math.max(-1, Math.min(1, (upper * upper + dist * dist - lower * lower) / (2 * upper * dist)));
  const angA = Math.acos(cosA);
  const elbowDir = new THREE.Vector3().copy(dir).multiplyScalar(Math.cos(angA)).addScaledVector(perp, Math.sin(angA));
  const reached = new THREE.Vector3().copy(shoulder).addScaledVector(dir, dist);
  aimWorld(a.arm, a.fore, elbowDir);
  rig.root.updateMatrixWorld(true);
  a.fore.getWorldPosition(elbow);
  const wristDir = new THREE.Vector3().copy(reached).sub(elbow);
  if (wristDir.lengthSq() > 1e-10) {
    wristDir.normalize();
    aimWorld(a.fore, a.hand, wristDir);
    rig.root.updateMatrixWorld(true);
  }
  if (t.twist) rotateWorld(a.hand, wristDir.lengthSq() > 1e-10 ? wristDir : AX_Y, sgn * t.twist);
  if (typeof t.curl === 'number' && t.curl !== 0) curlHand(rig, side, t.curl);
  return true;
}

/** Curl (or open) the fingers. `amount` is −1 (flat) … 0 (rest) … 1 (fist). */
export function curlHand(rig, side, amount) {
  const a = rig.arms[side];
  const v = Math.max(-1, Math.min(1, amount));
  for (const b of a.fingers) {
    b.rotation.x += THREE.MathUtils.degToRad(42 * v);
    b.updateMatrixWorld(true);
  }
  for (const b of a.thumb) {
    b.rotation.x += THREE.MathUtils.degToRad(16 * v);
    b.updateMatrixWorld(true);
  }
}

/**
 * Put the figure in a pose. Idempotent per mount: the caller applies it to a
 * freshly cloned skeleton (assembleAvatar does), never twice to the same one.
 */
export function applyPose(root, id, landmarks) {
  const pose = poseById(id);
  const rig = poseRigOf(root, landmarks);
  if (pose.spine) {
    rotateWorld(rig.spine, AX_X, pose.spine[0]);
    rotateWorld(rig.spine, AX_Y, pose.spine[1]);
    rotateWorld(rig.spine, AX_Z, -pose.spine[2]);
  }
  if (pose.head) {
    rotateWorld(rig.head, AX_X, pose.head[0]);
    rotateWorld(rig.head, AX_Y, pose.head[1]);
    rotateWorld(rig.head, AX_Z, -pose.head[2]);
  }
  if (pose.stance) {
    rotateWorld(rig.legs.Left, AX_Z, -pose.stance * 0.5);
    rotateWorld(rig.legs.Right, AX_Z, pose.stance * 0.5);
  }
  /* 🟢 every target is cleared against THIS body before it is solved, so the
     same pose holds on the slim build and the heavy one */
  if (pose.left) {
    const m = bodyMetrics(rig, 'Left');
    solveArm(rig, 'Left', clearedTarget(pose.left, m.S, m.hipDrop));
  }
  if (pose.right) {
    const m = bodyMetrics(rig, 'Right');
    solveArm(rig, 'Right', clearedTarget(pose.right, m.S, m.hipDrop));
  }
  root.updateMatrixWorld(true);
  return pose.id;
}
